import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from 'react-hook-form';
import { createMedicalLeaveFormSchema, type CreateMedicalLeaveFormValues } from '@/schemas/absence';
import { absenceService } from '@/api/domains/absence/absence.service';
import { ABSENCE_SCOPE_LIST_REFRESH_EVENT } from '@/api/domains/absence/absence.types';
import type {
  LC_MedicalLeaveTypeItemOut,
  LC_AbsenceWorkdayTypeItemOut,
} from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { useAbsenceEmployeeSelector } from '@/hooks/absence/useAbsenceEmployeeSelector';
import { useOverlapErrorBaseline } from '@/hooks/absence/useOverlapErrorBaseline';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { injectValidationErrors } from '@/utils';


const CALCULATED_DATES_DEBOUNCE_MS = 400;
const CONFLICT_DATE_OVERLAP_CODE = 'CONFLICT_DATE_OVERLAP';
const CONFLICT_EMPLOYEE_BLOCKED_CODE = 'CONFLICT_EMPLOYEE_BLOCKED';

export interface UseCreateMedicalLeaveResult {
  // Form
  form: {
    register: UseFormRegister<CreateMedicalLeaveFormValues>;
    setValue: UseFormSetValue<CreateMedicalLeaveFormValues>;
    watch: UseFormWatch<CreateMedicalLeaveFormValues>;
    errors: FieldErrors<CreateMedicalLeaveFormValues>;
    trigger: UseFormTrigger<CreateMedicalLeaveFormValues>;
  };
  isSubmitted: boolean;
  isSubmitting: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;

  // Employee selector
  employeeOptions: SelectorOption[];
  employeeBlockedIds: Set<number>;
  isLoadingEmployees: boolean;
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  handleEmployeeSearch: (search: string) => void;
  handleEmployeeChange: (value: string | string[]) => void;
  loadMoreEmployees: () => Promise<void>;

  // Leave types
  leaveTypeOptions: SelectorOption[];
  isLoadingLeaveTypes: boolean;

  // Workday types
  workdayTypeOptions: SelectorOption[];
  isLoadingWorkdayTypes: boolean;
}

export function useCreateMedicalLeave(): UseCreateMedicalLeaveResult {
  const { t: translate } = useTranslation('absence');
  const { showApiError, showSuccess, showNotification, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  // ── Leave types ────────────────────────────────────────────────────────────
  // Nota: no se usa AbortController aquí — es un catálogo pequeño de una sola pasada;
  // abortarlo en el cleanup generaría TaskCanceledException en el backend durante los
  // doble-montajes de React.StrictMode en desarrollo (ver useCatalogLoader.ts).
  const [leaveTypes, setLeaveTypes] = useState<LC_MedicalLeaveTypeItemOut[]>([]);
  const [isLoadingLeaveTypes, setIsLoadingLeaveTypes] = useState(false);

  const loadLeaveTypes = useCallback(async () => {
    setIsLoadingLeaveTypes(true);
    try {
      const response = await absenceService.getMedicalLeaveTypes();
      if (response.success && response.data) {
        setLeaveTypes(response.data);
      } else {
        showApiError(response, translate('medicalLeave.errors.loadLeaveTypesFailed'));
      }
    } finally {
      setIsLoadingLeaveTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadLeaveTypes();
  }, [loadLeaveTypes]);

  const leaveTypeOptions = useMemo<SelectorOption[]>(
    () => leaveTypes.map((type) => ({ value: String(type.id), label: type.name })),
    [leaveTypes],
  );

  // ── Workday types ──────────────────────────────────────────────────────────
  // Mismo motivo que loadLeaveTypes: catálogo estático, sin AbortController.
  const [workdayTypes, setWorkdayTypes] = useState<LC_AbsenceWorkdayTypeItemOut[]>([]);
  const [isLoadingWorkdayTypes, setIsLoadingWorkdayTypes] = useState(false);

  const loadWorkdayTypes = useCallback(async () => {
    setIsLoadingWorkdayTypes(true);
    try {
      const response = await absenceService.getAbsenceWorkdayTypes();
      if (response.success && response.data) {
        setWorkdayTypes(response.data);
      } else {
        showApiError(response, translate('medicalLeave.errors.loadWorkdayTypesFailed'));
      }
    } finally {
      setIsLoadingWorkdayTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadWorkdayTypes();
  }, [loadWorkdayTypes]);

  const workdayTypeOptions = useMemo<SelectorOption[]>(
    () => workdayTypes.map((type) => ({ value: type.code, label: type.label })),
    [workdayTypes],
  );

  // ── Employee selector ──────────────────────────────────────────────────────
  // Selector compartido con vacaciones: mismo endpoint, misma paginación y mismo bloqueo de
  // colaboradores sincronizados con Rex Remuneraciones (ver useAbsenceEmployeeSelector).
  const {
    employeeOptions,
    employeeBlockedIds,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    loadMoreEmployees,
    selectEmployee,
  } = useAbsenceEmployeeSelector({
    loadErrorMessage: translate('medicalLeave.errors.loadEmployeesFailed'),
    blockedTooltip: translate('medicalLeave.form.blockedEmployeeTooltip'),
  });

  // ── Loading state ──────────────────────────────────────────────────────────
  const initialLoadDoneRef = useRef(false);
  const areCatalogsLoaded = useMemo(
    () => !isLoadingEmployees && !isLoadingLeaveTypes && !isLoadingWorkdayTypes,
    [isLoadingEmployees, isLoadingLeaveTypes, isLoadingWorkdayTypes],
  );

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      setLoading(!areCatalogsLoaded);
      if (areCatalogsLoaded) {
        initialLoadDoneRef.current = true;
      }
    }
  }, [areCatalogsLoaded, setLoading]);

  // ── Form ───────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState,
  } = useForm<CreateMedicalLeaveFormValues>({
    resolver: zodResolver(createMedicalLeaveFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      employeeTargetId: null,
      leaveTypeId: null,
      startDate: '',
      endDate: '',
      folio: '',
      workdayType: undefined as unknown as string,
      dayCount: null,
    },
  });

  // Fecha de término y fecha de retorno se calculan en el backend a partir de fecha de inicio +
  // cantidad de días (mismo patrón que useCreateVacation.calculateDates).
  const calculatedDatesRequestIdRef = useRef(0);

  const startDateForDates = watch('startDate');
  const dayCountForDates = watch('dayCount');
  const employeeTargetIdForDates = watch('employeeTargetId');
  const workdayTypeForDates = watch('workdayType');
  const endDateValue = watch('endDate');

  // Los cuatro insumos comparten el mismo retardo: así una edición que toca varios campos dispara
  // una sola petición, y ninguna sale con una combinación a medio actualizar.
  const debouncedStartDateForDates = useDebounce(startDateForDates, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedDayCountForDates = useDebounce(dayCountForDates, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedEmployeeTargetIdForDates = useDebounce(
    employeeTargetIdForDates, CALCULATED_DATES_DEBOUNCE_MS,
  );
  const debouncedWorkdayTypeForDates = useDebounce(
    workdayTypeForDates, CALCULATED_DATES_DEBOUNCE_MS,
  );

  const calculateDates = useCallback(
    async (params: {
      employeeTargetId: number;
      startDate: string;
      dayCount: number;
      workdayType: string;
    }) => {
      const requestId = ++calculatedDatesRequestIdRef.current;
      const response = await absenceService.calculateAbsenceDates(params);
      if (requestId !== calculatedDatesRequestIdRef.current) return; // respuesta obsoleta, ignorar
      if (response.success && response.data) {
        setValue('endDate', response.data.endDate, { shouldDirty: true, shouldValidate: true });
        setValue('returnDate', response.data.returnDate, { shouldDirty: true });
      } else {
        setValue('endDate', '');
        setValue('returnDate', '');
        showApiError(response, translate('medicalLeave.errors.calculateDatesFailed'));
      }
    },
    [setValue, showApiError, translate],
  );

  useEffect(() => {
    const numericDayCount = Number(debouncedDayCountForDates);
    if (
      !debouncedEmployeeTargetIdForDates ||
      !debouncedStartDateForDates ||
      !debouncedWorkdayTypeForDates ||
      !Number.isInteger(numericDayCount) ||
      numericDayCount <= 0
    ) {
      setValue('endDate', '');
      setValue('returnDate', '');
      return;
    }
    void calculateDates({
      employeeTargetId: debouncedEmployeeTargetIdForDates as number,
      startDate: debouncedStartDateForDates,
      dayCount: numericDayCount,
      workdayType: debouncedWorkdayTypeForDates,
    });
  }, [
    debouncedEmployeeTargetIdForDates,
    debouncedStartDateForDates,
    debouncedDayCountForDates,
    debouncedWorkdayTypeForDates,
    calculateDates,
    setValue,
  ]);

  const { setBaseline: setOverlapErrorBaseline } = useOverlapErrorBaseline(
    startDateForDates, endDateValue, clearErrors,
  );

  const onValid = useCallback(
    async (data: CreateMedicalLeaveFormValues) => {
      setIsSubmitting(true);
      try {
        const response = await absenceService.createMedicalLeave({
          employeeTargetId: data.employeeTargetId as number,
          leaveTypeId: data.leaveTypeId as number,
          startDate: data.startDate,
          endDate: data.endDate,
          returnDate: data.returnDate || undefined,
          folio: data.folio,
          workdayType: data.workdayType,
          dayCount: data.dayCount as number,
        });

        if (response.success) {
          showSuccess(translate('medicalLeave.messages.createSuccess'));
          window.dispatchEvent(new CustomEvent(ABSENCE_SCOPE_LIST_REFRESH_EVENT));
          closeSidePanel?.();
        } else if (response.error?.code === CONFLICT_DATE_OVERLAP_CODE) {
          setOverlapErrorBaseline(data.startDate, data.endDate);
          setError('startDate', { message: 'absence:medicalLeave.errors.dateOverlap' });
          setError('endDate', { message: 'absence:medicalLeave.errors.dateOverlap' });
          showNotification('error', translate('medicalLeave.errors.dateOverlap'));
        } else if (response.error?.code === CONFLICT_EMPLOYEE_BLOCKED_CODE) {
          showNotification('error', translate('medicalLeave.form.blockedEmployeeTooltip'));
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('medicalLeave.errors.createFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      closeSidePanel, setError, setOverlapErrorBaseline, showApiError, showNotification,
      showSuccess, translate,
    ],
  );

  const handleEmployeeChange = useCallback(
    (value: string | string[]) => {
      const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
      const numericId = normalizeNumericSelectValue(stringValue);
      if (numericId !== undefined) {
        setValue('employeeTargetId', numericId as number, { shouldDirty: true });
        selectEmployee(numericId as number);
      }
    },
    [selectEmployee, setValue],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      const wrappedValid = (data: CreateMedicalLeaveFormValues) => { void onValid(data); };
      const wrappedInvalid = () => {};
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid],
  );

  return {
    form: {
      register,
      setValue,
      watch,
      errors: formState.errors,
      trigger,
    },
    isSubmitted: formState.isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    employeeBlockedIds,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    leaveTypeOptions,
    isLoadingLeaveTypes,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
  };
}

export default useCreateMedicalLeave;
